// src/pages/TasksPage/types.ts
export type Id = string;
export type ColorHex = string;

export type SpaceMode = "board" | "list";

export type StatusTask =
  | "Pendiente"
  | "En progreso"
  | "Bloqueada"
  | "Hecha"
  | "Listo";

export const ESTADOS_CONSULTA: {
  value: string;
  label: string;
  color: string;
}[] = [
  { value: "contactar", label: "Contactar", color: "#E57373" },
  { value: "en_conversación", label: "En conversación", color: "#FBC02D" },
  { value: "esperando_firma", label: "Esperando firma", color: "#64B5F6" },
  { value: "firmado", label: "Firmado", color: "#81C784" },
  { value: "en_preparacion", label: "En preparación", color: "#BA68C8" },
  { value: "listo", label: "Listo", color: "#4DB6AC" },
];

export const ESTADOS_SECTOR = [
  { value: "registrado", label: "Registrado", color: "#facc15" },
  { value: "en_proceso", label: "En proceso", color: "#38bdf8" },
  { value: "finalizado", label: "Finalizado", color: "#4ade80" },
  {
    value: "postergado",
    label: "Postergado por propietario",
    color: "#c084fc",
  },
];

export const PRIORIDADES_TAREA = [
  { value: "alta", label: "Alta", color: "#b91c1c" },
  { value: "media", label: "Media", color: "#facc15" },
  { value: "baja", label: "Baja", color: "#22c55e" },
  {
    value: "postergado",
    label: "Postergado por propietario",
    color: "#14b8a6",
  },
];

export type GerenciaImportancia = "Baja" | "Media" | "Alta" | "Crítica";

/**
 * Recurrencia de una tarea-plantilla. Cuando está seteada, el cron diario
 * del backend (`tareasRecurrentesCron`) clona una instancia (sin
 * recurrencia) cada vez que `proximaEl <= hoy` y avanza `proximaEl` al
 * siguiente ciclo. La tarea original sigue en la tabla, marcada con un
 * tag 🔁 para distinguirla.
 */
export type RecurrenciaTipo = "diaria" | "semanal" | "mensual";
export type Recurrencia = {
  tipo: RecurrenciaTipo;
  cada: number; // 1 = cada ciclo; 2 = cada 2 ciclos; etc.
  proximaEl: string; // YYYY-MM-DD
};

export type ColumnKey =
  | "item"
  | "propiedadIds"
  | "trabajadorId"
  | "prioridad"
  | "estado"
  | "registradoEl"
  | "terminadoEl"
  | "costo"
  | "imagenes"
  | "verCliente"
  // Consultas
  | "telefono"
  | "nombre"
  | "estadoConsulta"
  | "fecha"
  | "ubicacion"
  | "descripcion"
  // Gerencia
  | "importancia";

export type ColumnType =
  | "text"
  | "textarea"
  | "select"
  | "multiselect"
  | "date"
  | "daterange"
  | "number"
  | "boolean"
  | "attachments";

export type ColumnDef = {
  key: ColumnKey;
  title: string;
  type: ColumnType;
  width?: number;
};

export type Category = { id: Id; name: string };

export type Space = {
  id: Id;
  /**
   * FKs a `sector.id` con los que el espacio está vinculado. Cuando hay al
   * menos uno, la UI restringe la columna "Propiedad" a las propiedades
   * pertenecientes a alguno de esos sectores. Array vacío = sin filtro
   * (system spaces y legacy).
   */
  sectorIds?: number[];
  name: string;
  mode: SpaceMode; // ✅ board o list
  isSystem?: boolean; // ✅ Consultas/Gerencia fijos
  categories: Category[]; // board: se usa. list: vacío
  columns: ColumnDef[]; // ✅ columnas por space
};

export type TipoUsuario = "administrador" | "aseador" | "cliente";

export interface UsuarioActual {
  tipo: TipoUsuario;
  sectorIds?: Number[];
  propiedadIds?: Number[];
}

export interface Sector {
  id: string;
  nombre: string;
}

export type PropertyOption = {
  id: Id;
  name: string;
  color?: ColorHex | null;
  cliente: number;
  /**
   * Sector al que pertenece la propiedad (FK a `sector.id`). Se usa para
   * filtrar el dropdown "Propiedad" cuando el espacio activo está vinculado
   * a un sector.
   */
  sectorId?: number | null;
};
export type WorkerOption = { id: Id; name: string };
export type PriorityOption = { id: Id; name: string; color?: ColorHex | null };

export type Attachment = {
  id: Id;
  url: string;
  kind: "image" | "video";
  name: string;
  createdAt: string; // ISO
  /**
   * `key` estable del archivo en storage. Necesario para identificar el
   * attachment al editar metadatos (ej. comentario) — el orden del array
   * cambia con uploads/deletes, así que los índices no son confiables.
   */
  key?: string;
  /**
   * Miniatura webp de 400 px (firmada) que el backend agrega al serializar; en un video es
   * el fotograma. Las grillas y tiles la usan en vez de `url` (ver `utils/miniatura.ts`).
   * Puede venir nula o ausente en archivos viejos.
   */
  thumbUrl?: string | null;
  /**
   * URL firmada de la versión liviana (480p) de un video; `null` o ausente si no existe en
   * disco (videos viejos o aún en proceso). El reproductor abre con ella y ofrece pasar a HD.
   */
  previewUrl?: string | null;
  /** Peso (bytes) de la versión liviana, si existe. */
  previewSize?: number | null;
  /**
   * Comentario textual que admin/superadmin puede asociar a esta foto/video.
   * Visible al cliente cuando la tarea es compartida (`permitir_ver_cliente`).
   * `null` o ausente = sin comentario.
   * Ver backend/src/controllers/tasks.controller.js setAttachmentComment.
   */
  comentario?: string | null;
};

export type Task = {
  id: Id;
  spaceId: Id;
  categoryId?: Id | null;
  registradoPorId?: Number | null;
  // Campos “Tareas” (board)
  propiedadIds?: Number[] | null;
  trabajadorId?: Number | null;
  prioridad?: Id | null;
  estado?: StatusTask | string | null;
  registradoEl?: string | null;
  terminadoEl?: string | null;
  costo?: number | null;
  imagenes?: Attachment[];
  verCliente?: boolean;
  // Campos “Consultas”
  telefono?: string;
  nombre?: string;
  estadoConsulta?: string | null;
  fecha?: string | null;
  ubicacion?: string;
  descripcion?: string;
  // Campos “Gerencia”
  importancia?: GerenciaImportancia | null;
  // Plantilla recurrente (ver type Recurrencia). null/undefined = tarea normal.
  recurrencia?: Recurrencia | null;
  // UI-only: true mientras la creación optimista espera confirmación del
  // servidor. La fila se muestra con un spinner "Guardando…" y `id` temporal
  // (`tmp_*`) hasta que el backend responde y se reemplaza por la real. No se
  // persiste ni se envía al backend.
  _saving?: boolean;
};

export type NewTask = Omit<Task, "id">;

export type DB = {
  user: UsuarioActual;
  spaces: Space[];
  tasks: Task[];
  sectores: Sector[];
  propiedades: PropertyOption[];
  trabajadores: WorkerOption[];
  clientes: WorkerOption[];
  prioridades: PriorityOption[];

  estatusTareas: StatusTask[];
  estadoConsultas: string[];
  importanciaGerencia: GerenciaImportancia[];
};

export const columnsTareasDefault: ColumnDef[] = [
  { key: "descripcion", title: "Descripción", type: "text", width: 220 },
  { key: "verCliente", title: "Visible", type: "boolean", width: 70 },
  { key: "propiedadIds", title: "Propiedad", type: "multiselect", width: 200 },
  { key: "imagenes", title: "Adj", type: "attachments", width: 80 },
  { key: "trabajadorId", title: "Trabajador", type: "select", width: 140 },
  { key: "prioridad", title: "Pri", type: "select", width: 60 },
  { key: "estado", title: "Est", type: "select", width: 60 },
  { key: "registradoEl", title: "Registrado", type: "date", width: 130 },
  { key: "terminadoEl", title: "Terminado", type: "date", width: 130 },
  { key: "costo", title: "Costo", type: "number", width: 100 },
];

export const columnsConsultas: ColumnDef[] = [
  { key: "telefono", title: "Teléfono", type: "text", width: 160 },
  { key: "nombre", title: "Nombre", type: "text", width: 200 },
  { key: "estadoConsulta", title: "Estado", type: "select", width: 220 },
  { key: "fecha", title: "Fecha", type: "date", width: 150 },
  { key: "ubicacion", title: "Ubicación", type: "text", width: 220 },
  { key: "descripcion", title: "Descripción", type: "textarea", width: 360 },
];

export const columnsGerencia: ColumnDef[] = [
  { key: "descripcion", title: "Descripción", type: "textarea", width: 420 },
  { key: "fecha", title: "Fecha", type: "date", width: 160 },
  { key: "importancia", title: "Importancia", type: "select", width: 180 },
];

export type DeleteImageResp = {
  success: boolean;
  data?: { urls: string[] };
  message?: string;
  error?: string;
};
